import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { message, Popconfirm } from 'antd';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { getAudience } from '../../config/permissions';
import { SYSTEM_PAGE_CSS } from '../landlord/systemPageStyles';

interface SubscriptionStatus {
  subscription_status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

interface StripeStatus {
  connected: boolean;
  stripeAccountId: string | null;
  onboardingComplete: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
}

interface BusinessSettings {
  name: string;
  logo: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  website: string;
  companyNumber: string;
  vatNumber: string;
  taxRate: number;
  currency: string;
  timezone: string;
  paymentTerms: string;
  invoiceNotes: string;
  bankName: string;
  bankAccountName: string;
  bankSortCode: string;
  bankAccountNumber: string;
  working_hours: Record<string, { active: boolean; start: string; end: string }>;
}

const isBankComplete = (s: Pick<BusinessSettings, 'bankAccountName' | 'bankName' | 'bankSortCode' | 'bankAccountNumber'>) => {
  const sortDigits = String(s.bankSortCode || '').replace(/\D/g, '');
  const accountDigits = String(s.bankAccountNumber || '').replace(/\D/g, '');
  const accountName = String(s.bankAccountName || s.bankName || '').trim();
  return !!(accountName && sortDigits.length >= 6 && accountDigits.length >= 6);
};

const EMPTY: BusinessSettings = {
  name: '', logo: '', email: '', phone: '',
  address: '', city: '', state: '', zip: '', country: '',
  website: '', companyNumber: '', vatNumber: '',
  taxRate: 0, currency: 'GBP', timezone: 'Europe/London',
  paymentTerms: 'Net 30', invoiceNotes: '',
  bankName: '', bankAccountName: '', bankSortCode: '', bankAccountNumber: '',
  working_hours: {
    monday:    { active: true,  start: '09:00', end: '17:00' },
    tuesday:   { active: true,  start: '09:00', end: '17:00' },
    wednesday: { active: true,  start: '09:00', end: '17:00' },
    thursday:  { active: true,  start: '09:00', end: '17:00' },
    friday:    { active: true,  start: '09:00', end: '17:00' },
    saturday:  { active: false, start: '09:00', end: '17:00' },
    sunday:    { active: false, start: '09:00', end: '17:00' },
  },
};

type FieldOpts = {
  type?: string; placeholder?: string; required?: boolean;
  step?: string; span2?: boolean; textarea?: boolean;
  rows?: number; children?: React.ReactNode;
};

const FieldRow = ({ label, value, onChange, opts = {} }: {
  label: string; value: string | number;
  onChange: (v: string) => void; opts?: FieldOpts;
}) => (
  <div style={{ gridColumn: opts.span2 ? '1 / -1' : undefined }}>
    <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 5 }}>
      {label}{opts.required && <span style={{ color: '#ef4444' }}> *</span>}
    </label>
    {opts.children ? opts.children : opts.textarea ? (
      <textarea value={value} onChange={e => onChange(e.target.value)}
        placeholder={opts.placeholder} rows={opts.rows || 3}
        style={{ width: '100%', padding: '9px 11px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
    ) : (
      <input type={opts.type || 'text'} value={value} onChange={e => onChange(e.target.value)}
        placeholder={opts.placeholder} required={opts.required} step={opts.step}
        style={{ width: '100%', padding: '9px 11px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
    )}
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ background: '#fff', borderRadius: 12, padding: '24px 28px', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', marginBottom: 20 }}>
    <h2 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#111827', borderBottom: '1px solid #f3f4f6', paddingBottom: 12 }}>{title}</h2>
    {children}
  </div>
);

const grid2: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px 20px' };

const mkSel = (value: string, onChange: (v: string) => void, children: React.ReactNode) => (
  <select value={value} onChange={e => onChange(e.target.value)}
    style={{ width: '100%', padding: '9px 11px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }}>
    {children}
  </select>
);

// ─── Stripe status badge ───────────────────────────────────────────────────
const StripeBadge = ({ active, label }: { active: boolean; label: string }) => (
  <div style={{
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '5px 13px', borderRadius: 20,
    background: active ? '#f0fdf4' : '#f9fafb',
    border: `1px solid ${active ? '#86efac' : '#e5e7eb'}`,
    fontSize: 13, fontWeight: 600,
    color: active ? '#15803d' : '#9ca3af',
  }}>
    <div style={{
      width: 7, height: 7, borderRadius: '50%',
      background: active ? '#22c55e' : '#d1d5db',
      boxShadow: active ? '0 0 0 3px #dcfce7' : 'none',
    }} />
    {label}
  </div>
);

const BusinessSettings: React.FC = () => {
  const { token, logout, role } = useAuth();
  const navigate = useNavigate();

  // ── existing state ──────────────────────────────────────────────────────
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [previewLogo, setPreviewLogo]     = useState<string | null>(null);
  const [settings, setSettings]           = useState<BusinessSettings>(EMPTY);
  const [bankDetailsReady, setBankDetailsReady] = useState(false);
  const [subStatus, setSubStatus]         = useState<SubscriptionStatus | null>(null);
  const [canceling, setCanceling]         = useState(false);
  const [managingBilling, setManagingBilling] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [landlordPrefs, setLandlordPrefs] = useState({
    default_payment_day: 1, default_deposit_scheme: 'DPS', default_notice_period: 2,
    default_late_fee_amount: 25, default_late_fee_grace_days: 3, default_tenancy_type: 'AST',
    cert_expiry_emails_enabled: true,
  });
  const [savingLandlordPrefs, setSavingLandlordPrefs] = useState(false);
  const isLandlord = getAudience(localStorage.getItem('business_type')) === 'landlord';

  // ── stripe connect state ────────────────────────────────────────────────
  const [stripeStatus, setStripeStatus]               = useState<StripeStatus | null>(null);
  const [stripeLoading, setStripeLoading]             = useState(true);
  const [stripeConnecting, setStripeConnecting]       = useState(false);
  const [stripeDisconnecting, setStripeDisconnecting] = useState(false);
  const [stripeDashboard, setStripeDashboard]         = useState(false);

  // ── fetch stripe status ─────────────────────────────────────────────────
  const fetchStripeStatus = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_URL}/stripe/connect/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStripeStatus(await res.json());
    } catch { /* non-fatal */ } finally { setStripeLoading(false); }
  }, [token]);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchSettings();
    fetchSubStatus();
    fetchStripeStatus();
    if (getAudience(localStorage.getItem('business_type')) === 'landlord') {
      apiFetch(`${API_URL}/landlord/preferences`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.preferences) setLandlordPrefs(p => ({ ...p, ...d.preferences })); })
        .catch(() => undefined);
    }

    // Handle return from Stripe onboarding
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'success') {
      message.success('Stripe account connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      fetchStripeStatus();
    }
    if (params.get('stripe') === 'refresh') {
      message.info('Please complete your Stripe setup.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSubStatus = async () => {
    try {
      const res = await apiFetch(`${API_URL}/payments/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSubStatus(await res.json());
    } catch { /* non-fatal */ }
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      const res = await apiFetch(`${API_URL}/payments/cancel-subscription`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        message.success('Subscription canceled. Your access continues until the period end date.');
        await fetchSubStatus();
      } else {
        const data = await res.json();
        if (data.error === 'already_canceled') {
          message.warning('Your subscription is already canceled.');
          await fetchSubStatus();
        } else {
          message.error('Something went wrong. Please try again.');
        }
      }
    } catch {
      message.error('Something went wrong. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  const handleManageBilling = async () => {
    setManagingBilling(true);
    try {
      const res = await apiFetch(`${API_URL}/stripe/portal-session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        message.error(data.error || 'Could not open billing portal.');
      }
    } catch {
      message.error('Could not open billing portal.');
    } finally {
      setManagingBilling(false);
    }
  };

  const renderSubscriptionSection = () => {
    if (!subStatus) {
      return <p style={{ color: '#9ca3af', fontSize: 14 }}>Loading subscription…</p>;
    }
    const status = subStatus.subscription_status || 'unknown';
    const statusColors: Record<string, { bg: string; color: string }> = {
      active: { bg: '#dcfce7', color: '#166534' },
      trialing: { bg: '#dbeafe', color: '#1d4ed8' },
      past_due: { bg: '#fef3c7', color: '#92400e' },
      canceled: { bg: '#fee2e2', color: '#dc2626' },
    };
    const sc = statusColors[status] || { bg: '#f3f4f6', color: '#374151' };
    const periodEnd = subStatus.current_period_end
      ? new Date(subStatus.current_period_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      : null;

    return (
      <div>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap', marginBottom: 16,
          padding: '18px 22px', background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 22 }}>💳</span>
              <span style={{ fontWeight: 800, fontSize: 15, color: '#111827' }}>ShahoSuite Subscription</span>
              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: sc.bg, color: sc.color, textTransform: 'capitalize' }}>
                {status.replace('_', ' ')}
              </span>
            </div>
            {periodEnd && (
              <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
                {subStatus.cancel_at_period_end
                  ? `Access until ${periodEnd} (cancellation scheduled)`
                  : `Current period ends ${periodEnd}`}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleManageBilling}
              disabled={managingBilling}
              style={{
                padding: '9px 20px', background: '#2563eb', color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
                cursor: managingBilling ? 'not-allowed' : 'pointer',
              }}
            >
              {managingBilling ? 'Opening…' : 'Manage billing'}
            </button>
            {status === 'active' && !subStatus.cancel_at_period_end && (
              <Popconfirm
                title="Cancel subscription?"
                description="Your access continues until the end of the current billing period."
                onConfirm={handleCancelSubscription}
                okText="Cancel subscription"
                cancelText="Keep"
                okButtonProps={{ danger: true, loading: canceling }}
              >
                <button type="button" style={{
                  padding: '9px 20px', background: '#fff', color: '#dc2626',
                  border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}>
                  Cancel plan
                </button>
              </Popconfirm>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      const res = await apiFetch(`${API_URL}/payments/delete-account`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        message.success('Your account has been deleted. You will be logged out.');
        setTimeout(() => { logout(); navigate('/login'); }, 2000);
      } else {
        message.error('Failed to delete account. Please contact support.');
      }
    } catch {
      message.error('Something went wrong. Please try again.');
    } finally {
      setDeletingAccount(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await apiFetch(`${API_URL}/business`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`${res.status}`);
      const d = await res.json();
      setSettings({
        name:          d.name           || '',
        logo:          d.logo           || '',
        email:         d.email          || '',
        phone:         d.phone          || '',
        address:       d.address        || '',
        city:          d.city           || '',
        state:         d.state          || '',
        zip:           d.zip            || '',
        country:       d.country        || '',
        website:       d.website        || '',
        companyNumber: d.companyNumber  || d.company_number || '',
        vatNumber:     d.vatNumber      || d.vat_number     || '',
        taxRate:       d.taxRate        !== undefined ? d.taxRate : (d.tax_rate !== undefined ? d.tax_rate : 0),
        currency:      d.currency       || 'GBP',
        timezone:      d.timezone       || 'Europe/London',
        paymentTerms:  d.paymentTerms   || d.payment_terms  || 'Net 30',
        invoiceNotes:  d.invoiceNotes   || d.invoice_notes  || '',
        bankName:          d.bankName          || d.bank_name           || '',
        bankAccountName:   d.bankAccountName   || d.bank_account_name   || '',
        bankSortCode:      d.bankSortCode      || d.bank_sort_code      || '',
        bankAccountNumber: d.bankAccountNumber || d.bank_account_number || '',
        working_hours: d.working_hours || EMPTY.working_hours,
      });
      setBankDetailsReady(!!d.bankDetailsReady || isBankComplete({
        bankName: d.bankName || d.bank_name || '',
        bankAccountName: d.bankAccountName || d.bank_account_name || '',
        bankSortCode: d.bankSortCode || d.bank_sort_code || '',
        bankAccountNumber: d.bankAccountNumber || d.bank_account_number || '',
      }));
    } catch (e: any) {
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const set = (f: keyof BusinessSettings) => (value: string) =>
    setSettings(prev => ({ ...prev, [f]: f === 'taxRate' ? parseFloat(value) || 0 : value }));

  const handleSave = async () => {
    if (!settings.name.trim()) { message.error('Business name is required'); return; }
    setSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/business`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(settings),
      });
      if (!res.ok) {
        if (res.status === 403) {
          message.error('Only admins can save business settings. Ask your admin to save bank details.');
          return;
        }
        throw new Error(`${res.status}`);
      }
      const saved = await res.json();
      setBankDetailsReady(!!saved.bankDetailsReady);
      if (saved.bankDetailsReady) {
        message.success('Settings saved — bank details will appear in order and invoice emails');
      } else if (settings.bankSortCode || settings.bankAccountNumber) {
        message.warning('Settings saved, but bank details are incomplete. Add account name, sort code (6 digits), and account number.');
      } else {
        message.success('Settings saved successfully');
      }
      await fetchSettings();
    } catch (e: any) {
      message.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { message.error('Please upload an image file'); return; }
    if (file.size > 2 * 1024 * 1024)    { message.error('Logo must be less than 2MB');  return; }
    // Instant preview before upload
    const preview = URL.createObjectURL(file);
    setPreviewLogo(preview);
    setUploadingLogo(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await apiFetch(`${API_URL}/business/upload-logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSettings(prev => ({ ...prev, logo: data.logoUrl }));
      setPreviewLogo(null);
      message.success('Logo uploaded');
    } catch {
      setPreviewLogo(null);
      message.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // ── stripe connect handlers ─────────────────────────────────────────────
  const handleStripeConnect = async () => {
    setStripeConnecting(true);
    try {
      const res = await apiFetch(`${API_URL}/stripe/connect/onboard`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      window.location.href = data.url;
    } catch {
      message.error('Failed to start Stripe setup. Please try again.');
      setStripeConnecting(false);
    }
  };

  const handleStripeDashboard = async () => {
    setStripeDashboard(true);
    try {
      const res = await apiFetch(`${API_URL}/stripe/connect/dashboard-link`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      window.open(data.url, '_blank');
    } catch {
      message.error('Failed to open Stripe dashboard. Please try again.');
    } finally {
      setStripeDashboard(false);
    }
  };

  const handleStripeDisconnect = async () => {
    setStripeDisconnecting(true);
    try {
      const res = await apiFetch(`${API_URL}/stripe/connect/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      message.success('Stripe account disconnected.');
      await fetchStripeStatus();
    } catch {
      message.error('Failed to disconnect. Please try again.');
    } finally {
      setStripeDisconnecting(false);
    }
  };

  const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
  const setWH = (day: string, field: string, val: any) =>
    setSettings(prev => ({
      ...prev,
      working_hours: { ...prev.working_hours, [day]: { ...prev.working_hours[day], [field]: val } }
    }));

  if (loading) {
    return (
      <div className="page-layout">
        <div style={{ textAlign: 'center', padding: 80, color: '#9ca3af' }}>Loading settings...</div>
      </div>
    );
  }

  const SaveBtn = ({ large = false }: { large?: boolean }) => (
    isLandlord ? (
      <button type="button" onClick={handleSave} disabled={saving} className="sys-btn-save">
        {saving ? 'Saving…' : '💾 Save'}
      </button>
    ) : (
      <button onClick={handleSave} disabled={saving} style={{
        padding: large ? '12px 32px' : '10px 24px',
        background: saving ? '#9ca3af' : '#2563eb',
        color: '#fff', border: 'none', borderRadius: 8,
        fontSize: large ? 15 : 14, fontWeight: 700,
        cursor: saving ? 'not-allowed' : 'pointer',
      }}>
        {saving ? 'Saving...' : 'Save Settings'}
      </button>
    )
  );

  // ── stripe connect UI ───────────────────────────────────────────────────
  const renderStripeSection = () => {
    if (stripeLoading) {
      return <p style={{ color: '#9ca3af', fontSize: 14 }}>Loading payment settings...</p>;
    }

    // NOT CONNECTED
    if (!stripeStatus?.connected) {
      return (
        <div>
          {/* Hero banner */}
          <div style={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            borderRadius: 14, padding: '28px 32px', marginBottom: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 24, flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 28 }}>💳</span>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#fff' }}>
                  Accept Payments Directly
                </h3>
              </div>
              <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, maxWidth: 440 }}>
                Connect your Stripe account and start accepting card payments for bookings,
                invoices and shop purchases. Money goes straight to your bank —
                <strong style={{ color: '#fff' }}> ShahoSuite takes 0%</strong>.
              </p>
            </div>
            <button
              onClick={handleStripeConnect}
              disabled={stripeConnecting}
              style={{
                padding: '13px 28px',
                background: stripeConnecting ? 'rgba(255,255,255,0.3)' : '#fff',
                color: stripeConnecting ? '#fff' : '#7c3aed',
                border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 800,
                cursor: stripeConnecting ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
                transition: 'all 0.2s',
              }}
            >
              {stripeConnecting ? 'Redirecting...' : 'Connect Stripe Account →'}
            </button>
          </div>

          {/* Feature cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[
              { icon: '⚡', title: 'Quick setup', desc: 'Connect in under 5 minutes. Stripe handles all verification.' },
              { icon: '🏦', title: 'Daily payouts', desc: 'Payments land in your bank automatically every day.' },
              { icon: '🔒', title: '0% platform fee', desc: 'Keep 100% of your revenue. Only Stripe\'s card fees apply.' },
            ].map(f => (
              <div key={f.title} style={{
                background: '#f9fafb', borderRadius: 10,
                border: '1px solid #f3f4f6', padding: '16px 18px',
              }}>
                <div style={{ fontSize: 22, marginBottom: 7 }}>{f.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#111827', marginBottom: 4 }}>{f.title}</div>
                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // CONNECTED BUT INCOMPLETE
    if (stripeStatus.connected && !stripeStatus.onboardingComplete) {
      return (
        <div>
          <div style={{
            background: '#fffbeb', border: '1px solid #fde68a',
            borderRadius: 12, padding: '22px 26px', marginBottom: 16,
            display: 'flex', alignItems: 'flex-start', gap: 14,
          }}>
            <span style={{ fontSize: 26, flexShrink: 0 }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e', marginBottom: 6 }}>
                Finish setting up your Stripe account
              </div>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: '#78350f', lineHeight: 1.6 }}>
                Your account was created but verification is not complete.
                You won't be able to accept payments until you finish setup.
              </p>
              <button
                onClick={handleStripeConnect}
                disabled={stripeConnecting}
                style={{
                  padding: '9px 22px',
                  background: stripeConnecting ? '#9ca3af' : '#d97706',
                  color: '#fff', border: 'none', borderRadius: 8,
                  fontSize: 13, fontWeight: 700,
                  cursor: stripeConnecting ? 'not-allowed' : 'pointer',
                }}
              >
                {stripeConnecting ? 'Loading...' : 'Complete Stripe Setup →'}
              </button>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <button onClick={handleStripeDisconnect} disabled={stripeDisconnecting}
              style={{ padding: '7px 16px', background: 'none', color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {stripeDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </button>
          </div>
        </div>
      );
    }

    // FULLY CONNECTED
    return (
      <div>
        {/* Connected banner */}
        <div style={{
          background: '#f0fdf4', border: '1px solid #86efac',
          borderRadius: 12, padding: '18px 22px', marginBottom: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 10, background: '#dcfce7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, flexShrink: 0,
            }}>✅</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#15803d' }}>Stripe account connected</div>
              <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2, fontFamily: 'monospace' }}>
                {stripeStatus.stripeAccountId}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <StripeBadge active={stripeStatus.chargesEnabled} label="Charges enabled" />
            <StripeBadge active={stripeStatus.payoutsEnabled} label="Payouts enabled" />
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
          <button
            onClick={handleStripeDashboard}
            disabled={stripeDashboard}
            style={{
              padding: '10px 22px',
              background: stripeDashboard ? '#9ca3af' : '#7c3aed',
              color: '#fff', border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 700,
              cursor: stripeDashboard ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 8px rgba(124,58,237,0.25)',
            }}
          >
            {stripeDashboard ? 'Loading...' : '📊 View Stripe Dashboard'}
          </button>
          <button
            onClick={handleStripeConnect}
            disabled={stripeConnecting}
            style={{
              padding: '10px 22px', background: '#fff',
              color: '#374151', border: '1px solid #d1d5db', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {stripeConnecting ? 'Loading...' : '⚙️ Update Stripe Settings'}
          </button>
        </div>

        {/* Stats strip */}
        <div style={{
          background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0',
          padding: '14px 18px', marginBottom: 18,
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12,
        }}>
          {[
            { label: 'Platform fee', value: '0%', sub: 'ShahoSuite keeps nothing' },
            { label: 'Payout schedule', value: 'Daily', sub: 'Automatic bank transfers' },
            { label: 'Card processing', value: 'Stripe rates', sub: '1.5% + 20p typical UK card' },
          ].map(item => (
            <div key={item.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{item.value}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginTop: 2 }}>{item.label}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{item.sub}</div>
            </div>
          ))}
        </div>

        {/* Disconnect */}
        <div style={{ borderTop: '1px solid #fee2e2', paddingTop: 14 }}>
          <p style={{ margin: '0 0 10px', fontSize: 12, color: '#9ca3af' }}>
            Disconnecting will prevent new payments. Your Stripe account itself will not be deleted.
          </p>
          <button
            onClick={handleStripeDisconnect}
            disabled={stripeDisconnecting}
            style={{
              padding: '7px 16px', background: 'none',
              color: '#dc2626', border: '1px solid #fca5a5',
              borderRadius: 7, fontSize: 12, fontWeight: 600,
              cursor: stripeDisconnecting ? 'not-allowed' : 'pointer',
            }}
          >
            {stripeDisconnecting ? 'Disconnecting...' : 'Disconnect Stripe Account'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={isLandlord ? 'sys-page' : 'page-layout'}>
      {isLandlord && <style>{SYSTEM_PAGE_CSS}</style>}
      {isLandlord ? (
        <div className="sys-banner-wrap sys-banner-uk">
          <div className="sys-banner-glass">
            <div>
              <h1>⚙️ Settings</h1>
              <p>Business profile, bank details &amp; landlord defaults — {settings.name || 'your portfolio'}</p>
            </div>
            <div className="sys-banner-actions"><SaveBtn /></div>
          </div>
        </div>
      ) : (
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>Settings</h1>
            <p>Manage your business profile, financial preferences, and invoice defaults</p>
          </div>
          <SaveBtn />
        </div>
      )}

      {/* 1. Business Information */}
      <Section title="Business Information">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 24 }}>
          <div style={{
            width: 100, height: 100, borderRadius: 12, border: '2px dashed #d1d5db',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', background: '#f9fafb', flexShrink: 0,
          }}>
            {(previewLogo || settings.logo)
              ? <img src={previewLogo || settings.logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 36 }}>🏢</span>}
          </div>
          <div style={{ paddingTop: 6 }}>
            <label htmlFor="logo-upload" style={{
              display: 'inline-block', padding: '7px 16px', background: '#f3f4f6',
              border: '1px solid #d1d5db', borderRadius: 7,
              cursor: uploadingLogo ? 'wait' : 'pointer',
              fontSize: 13, fontWeight: 600, marginBottom: 6,
            }}>
              {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
            </label>
            <input id="logo-upload" type="file" accept="image/*" onChange={handleLogoUpload}
              style={{ display: 'none' }} disabled={uploadingLogo} />
            <p style={{ margin: '0 0 4px', fontSize: 12, color: '#9ca3af' }}>
              Recommended: 200x200px, max 2MB (PNG or JPG)
            </p>
            {settings.logo && (
              <button onClick={() => setSettings(p => ({ ...p, logo: '' }))} style={{
                background: 'none', border: 'none', color: '#ef4444',
                cursor: 'pointer', fontSize: 12, padding: 0,
              }}>Remove logo</button>
            )}
          </div>
        </div>

        <div style={grid2}>
          <FieldRow label="Business Name" value={settings.name} onChange={set('name')}
            opts={{ required: true, placeholder: 'Acme Ltd' }} />
          <FieldRow label="Email" value={settings.email} onChange={set('email')}
            opts={{ required: true, type: 'email', placeholder: 'contact@acme.com' }} />
          <FieldRow label="Phone" value={settings.phone} onChange={set('phone')}
            opts={{ placeholder: '+44 7700 900123' }} />
          <FieldRow label="Website" value={settings.website} onChange={set('website')}
            opts={{ placeholder: 'https://acme.com' }} />
          <FieldRow label="Company Registration Number" value={settings.companyNumber} onChange={set('companyNumber')}
            opts={{ placeholder: '12345678' }} />
          <FieldRow label="VAT / Tax Number" value={settings.vatNumber} onChange={set('vatNumber')}
            opts={{ placeholder: 'GB123456789' }} />
        </div>
      </Section>

      {/* 2. Address */}
      <Section title="Address">
        <div style={grid2}>
          <FieldRow label="Street Address" value={settings.address} onChange={set('address')}
            opts={{ span2: true, placeholder: '10 Downing Street' }} />
          <FieldRow label="City" value={settings.city} onChange={set('city')}
            opts={{ placeholder: 'London' }} />
          <FieldRow label="State / Province" value={settings.state} onChange={set('state')}
            opts={{ placeholder: 'England' }} />
          <FieldRow label="ZIP / Postal Code" value={settings.zip} onChange={set('zip')}
            opts={{ placeholder: 'SW1A 2AA' }} />
          <FieldRow label="Country" value={settings.country} onChange={set('country')}
            opts={{ placeholder: 'United Kingdom' }} />
        </div>
      </Section>

      {/* 3. Financial Settings */}
      <Section title="Financial Settings">
        <div style={grid2}>
          <FieldRow label="Tax Rate (%)" value={settings.taxRate} onChange={set('taxRate')}
            opts={{ type: 'number', step: '0.01', placeholder: '20' }} />
          <FieldRow label="Currency" value={settings.currency} onChange={set('currency')} opts={{ children:
            mkSel(settings.currency, set('currency'), <>
              <option value="GBP">GBP - British Pound</option>
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="AUD">AUD - Australian Dollar</option>
              <option value="AED">AED - UAE Dirham</option>
            </>)
          }} />
          <FieldRow label="Timezone" value={settings.timezone} onChange={set('timezone')} opts={{ children:
            mkSel(settings.timezone, set('timezone'), <>
              <option value="Europe/London">London (GMT/BST)</option>
              <option value="Europe/Paris">Paris (CET)</option>
              <option value="America/New_York">New York (ET)</option>
              <option value="America/Chicago">Chicago (CT)</option>
              <option value="America/Denver">Denver (MT)</option>
              <option value="America/Los_Angeles">Los Angeles (PT)</option>
              <option value="Asia/Dubai">Dubai (GST)</option>
              <option value="UTC">UTC</option>
            </>)
          }} />
        </div>
      </Section>

      <Section title="Subscription & Billing">
        {renderSubscriptionSection()}
      </Section>

      {!isLandlord && (
        <Section title="Stripe Connect — Accept Payments">
          {renderStripeSection()}
        </Section>
      )}

      {/* 3b. Bank Transfer Details */}
      <Section title="Bank Transfer Details">
        <div style={{
          margin: '0 0 16px', padding: '12px 14px', borderRadius: 8, fontSize: 13,
          background: (bankDetailsReady || isBankComplete(settings)) ? '#f0fdf4' : '#fffbeb',
          border: `1px solid ${(bankDetailsReady || isBankComplete(settings)) ? '#bbf7d0' : '#fde68a'}`,
          color: (bankDetailsReady || isBankComplete(settings)) ? '#166534' : '#92400e',
        }}>
          {(bankDetailsReady || isBankComplete(settings))
            ? `✓ Bank details are complete — they will appear in ${isLandlord ? 'rent reminder emails, tenant statements, and' : ''} shop order emails, invoices, and payment reminders.`
            : '⚠ Bank details missing or incomplete — shop customers will see "Please contact us for payment instructions" instead of your bank account. Fill in all fields below and click Save Settings (admin only).'}
        </div>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
          Sort code and account number are required. Saving other settings no longer clears bank details once entered.
        </p>
        <div style={grid2}>
          <FieldRow label="Bank Name" value={settings.bankName} onChange={set('bankName')}
            opts={{ placeholder: 'e.g. Barclays' }} />
          <FieldRow label="Account Name" value={settings.bankAccountName} onChange={set('bankAccountName')}
            opts={{ placeholder: 'e.g. My Business Ltd' }} />
          <FieldRow label="Sort Code" value={settings.bankSortCode} onChange={set('bankSortCode')}
            opts={{ placeholder: '12-34-56' }} />
          <FieldRow label="Account Number" value={settings.bankAccountNumber} onChange={set('bankAccountNumber')}
            opts={{ placeholder: '12345678' }} />
        </div>
      </Section>

      {isLandlord && (
        <Section title="🏠 Landlord Preferences">
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
            Defaults pre-fill new tenancies and Quick Let onboarding. Bank details above are included in rent reminder emails.
          </p>
          <div style={grid2}>
            <FieldRow label="Default Rent Due Day" value={String(landlordPrefs.default_payment_day)} onChange={v => setLandlordPrefs(p => ({ ...p, default_payment_day: Number(v) || 1 }))} opts={{ children:
              <select value={String(landlordPrefs.default_payment_day)} onChange={e => setLandlordPrefs(p => ({ ...p, default_payment_day: Number(e.target.value) }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'} of month</option>)}
              </select>
            }} />
            <FieldRow label="Default Deposit Scheme" value={landlordPrefs.default_deposit_scheme} onChange={v => setLandlordPrefs(p => ({ ...p, default_deposit_scheme: v }))} opts={{ children:
              <select value={landlordPrefs.default_deposit_scheme} onChange={e => setLandlordPrefs(p => ({ ...p, default_deposit_scheme: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}>
                <option value="DPS">DPS</option><option value="TDS">TDS</option><option value="MyDeposits">MyDeposits</option>
              </select>
            }} />
            <FieldRow label="Default Notice Period (months)" value={String(landlordPrefs.default_notice_period)} onChange={v => setLandlordPrefs(p => ({ ...p, default_notice_period: Number(v) || 2 }))} opts={{ children:
              <select value={String(landlordPrefs.default_notice_period)} onChange={e => setLandlordPrefs(p => ({ ...p, default_notice_period: Number(e.target.value) }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}>
                {[1, 2, 3, 6].map(n => <option key={n} value={n}>{n} month{n > 1 ? 's' : ''}</option>)}
              </select>
            }} />
            <FieldRow label="Default Tenancy Type" value={landlordPrefs.default_tenancy_type} onChange={v => setLandlordPrefs(p => ({ ...p, default_tenancy_type: v }))} opts={{ children:
              <select value={landlordPrefs.default_tenancy_type} onChange={e => setLandlordPrefs(p => ({ ...p, default_tenancy_type: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}>
                <option value="AST">AST</option><option value="Periodic">Periodic</option><option value="Fixed">Fixed Term</option>
              </select>
            }} />
            <FieldRow label="Default Late Fee (£)" value={String(landlordPrefs.default_late_fee_amount)} onChange={v => setLandlordPrefs(p => ({ ...p, default_late_fee_amount: Number(v) || 0 }))} opts={{ type: 'number', placeholder: '25' }} />
            <FieldRow label="Late Fee Grace Days" value={String(landlordPrefs.default_late_fee_grace_days)} onChange={v => setLandlordPrefs(p => ({ ...p, default_late_fee_grace_days: Number(v) || 3 }))} opts={{ type: 'number', placeholder: '3' }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={landlordPrefs.cert_expiry_emails_enabled !== false}
              onChange={e => setLandlordPrefs(p => ({ ...p, cert_expiry_emails_enabled: e.target.checked }))}
              style={{ width: 18, height: 18, accentColor: '#0d9488' }} />
            Send automated certificate &amp; lease expiry email alerts (60/30/14/7 days)
          </label>
          <button onClick={async () => {
            setSavingLandlordPrefs(true);
            try {
              const res = await apiFetch(`${API_URL}/landlord/preferences`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ preferences: landlordPrefs }),
              });
              if (res.ok) message.success('Landlord preferences saved');
              else message.error('Failed to save preferences');
            } catch { message.error('Failed to save preferences'); }
            setSavingLandlordPrefs(false);
          }} disabled={savingLandlordPrefs} style={{ marginTop: 12, background: '#0d9488', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 700, cursor: 'pointer' }}>
            {savingLandlordPrefs ? 'Saving…' : 'Save Landlord Preferences'}
          </button>
        </Section>
      )}

      {/* 4. Invoice Defaults */}
      <Section title="Invoice Defaults">
        <div style={grid2}>
          <FieldRow label="Default Payment Terms" value={settings.paymentTerms} onChange={set('paymentTerms')} opts={{ children:
            mkSel(settings.paymentTerms, set('paymentTerms'), <>
              <option value="Due on Receipt">Due on Receipt</option>
              <option value="Net 7">Net 7 - due in 7 days</option>
              <option value="Net 14">Net 14 - due in 14 days</option>
              <option value="Net 30">Net 30 - due in 30 days</option>
              <option value="Net 60">Net 60 - due in 60 days</option>
              <option value="Net 90">Net 90 - due in 90 days</option>
            </>)
          }} />
          <FieldRow label="Default Invoice Notes / Footer" value={settings.invoiceNotes} onChange={set('invoiceNotes')}
            opts={{
              span2: true, textarea: true, rows: 3,
              placeholder: 'e.g. Thank you for your business. Payment due within the agreed terms.',
            }} />
        </div>
        <p style={{ margin: '12px 0 0', fontSize: 12, color: '#9ca3af' }}>
          These defaults pre-fill every new invoice but can be overridden per invoice.
        </p>
      </Section>

      {/* 5. Working Hours */}
      <Section title="🕐 My Working Hours (Director)">
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
          Set your available hours for customer bookings.
          These are used when customers book directly with you.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {DAYS.map(day => {
            const wh = settings.working_hours?.[day] || { active: false, start: '09:00', end: '17:00' };
            return (
              <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: wh.active ? '#f0fdf4' : '#f9fafb', borderRadius: 8, border: `1px solid ${wh.active ? '#86efac' : '#e5e7eb'}` }}>
                <input type="checkbox" checked={!!wh.active}
                  onChange={e => setWH(day, 'active', e.target.checked)}
                  style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
                <span style={{ width: 100, fontSize: 14, fontWeight: 600, textTransform: 'capitalize', color: wh.active ? '#15803d' : '#9ca3af' }}>
                  {day}
                </span>
                {wh.active ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="time" value={wh.start} onChange={e => setWH(day, 'start', e.target.value)}
                      style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }} />
                    <span style={{ color: '#6b7280', fontSize: 13 }}>to</span>
                    <input type="time" value={wh.end} onChange={e => setWH(day, 'end', e.target.value)}
                      style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }} />
                    <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 4 }}>
                      {(() => {
                        const [sh, sm] = wh.start.split(':').map(Number);
                        const [eh, em] = wh.end.split(':').map(Number);
                        const mins = (eh * 60 + em) - (sh * 60 + sm);
                        return mins > 0 ? `${Math.floor(mins/60)}h ${mins%60 > 0 ? mins%60+'m' : ''}`.trim() : '';
                      })()}
                    </span>
                  </div>
                ) : (
                  <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>Closed</span>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* 6. Email Delivery */}
      <Section title="Email Delivery">
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 14,
          background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '16px 20px',
        }}>
          <span style={{ fontSize: 28, flexShrink: 0, marginTop: 2 }}>☁️</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#0369a1', marginBottom: 4 }}>
              Powered by Azure Communication Services — no setup required
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#0c4a6e', lineHeight: 1.6 }}>
              All transactional emails — invoices, booking confirmations, payment receipts,
              staff invitations, and support replies — are delivered automatically via Azure
              Communication Services. No SMTP credentials, no passwords, no configuration needed.
            </p>
          </div>
        </div>
      </Section>

      <div style={{ textAlign: 'right', marginBottom: 48 }}>
        <SaveBtn large={true} />
      </div>


    </div>
  );
};

export default BusinessSettings;